/*
 * DuplicarNegociacionModal.jsx
 *
 * Permite clonar una negociación existente (con su service_offer y % bajada)
 * a N sites de la cartera. Filtra por tipo / provincia / entorno, muestra
 * renta anual y fin de contrato y bloquea sites con negociación activa.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { XCircle, Copy, Search, Loader2, CheckSquare, Square } from 'lucide-react';
import api from '../api/client';
import { toast } from 'sonner';

const eur = (v) => (Number(v) || 0).toLocaleString('es-ES', { maximumFractionDigits: 0 });

const DuplicarNegociacionModal = ({ neg, onClose, onCloned }) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [q, setQ] = useState('');
  const [fType, setFType] = useState('');
  const [fProvince, setFProvince] = useState('');

  // % que se replicará (calculado desde la negociación fuente).
  const srcPct = useMemo(() => {
    const c = Number(neg.current_rent_monthly) || 0;
    const t = Number(neg.target_rent_monthly) || 0;
    return c > 0 ? ((c - t) / c) * 100 : 0;
  }, [neg]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fType) params.set('type', fType);
      if (fProvince) params.set('province', fProvince);
      params.set('limit', 300);
      const r = await api.get(`/rent-reduction/${neg.id}/clone-candidates?${params.toString()}`);
      setCandidates(r.data || []);
    } catch (e) {
      toast.error('No se pudieron cargar los candidatos');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [fType, fProvince]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return candidates;
    return candidates.filter((c) =>
      (c.code || '').toLowerCase().includes(term) ||
      (c.municipality || '').toLowerCase().includes(term) ||
      (c.owner_name || '').toLowerCase().includes(term));
  }, [candidates, q]);

  const eligibles = useMemo(() =>
    filtered.filter((c) => !c.has_active_negotiation && (c.rent_annual || 0) > 0),
  [filtered]);

  const toggle = (code) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(code)) n.delete(code); else n.add(code);
    return n;
  });
  const toggleAll = () => setSelected((prev) => {
    if (prev.size === eligibles.length) return new Set();
    return new Set(eligibles.map((c) => c.code));
  });

  const clone = async () => {
    if (selected.size === 0) return toast.error('Selecciona al menos un site');
    setSaving(true);
    try {
      const { data } = await api.post(`/rent-reduction/${neg.id}/clone-to-sites`, {
        site_ids: Array.from(selected),
        same_pct: true,
      });
      const msg = `Creadas ${data.created} · omitidas ${data.skipped}`;
      if (data.created > 0) toast.success(msg);
      else toast.error(msg);
      onCloned?.(data);
      if (data.created > 0) onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error clonando');
    } finally { setSaving(false); }
  };

  // Opciones únicas de tipo / provincia (para filtros).
  const typeOptions = useMemo(() => {
    return Array.from(new Set(candidates.map((c) => c.type).filter(Boolean))).sort();
  }, [candidates]);
  const provinceOptions = useMemo(() => {
    return Array.from(new Set(candidates.map((c) => c.province).filter(Boolean))).sort();
  }, [candidates]);

  return (
    <div className="fixed inset-0 z-[10090] bg-black/90 backdrop-blur flex items-center justify-center p-2 sm:p-4"
      onClick={onClose} data-testid="duplicar-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-5xl max-h-[92vh] flex flex-col bg-[#0a0a0a] border border-white/15 rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-white/10 bg-gradient-to-r from-[#A3FF12]/10 to-transparent">
          <div className="flex items-center gap-2 min-w-0">
            <Copy size={16} className="text-[#A3FF12]"/>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-[#A3FF12] font-black">Duplicar negociación</div>
              <div className="text-white text-sm font-black truncate">
                Desde {neg.site_id} · <span className="text-[#A3FF12]">{srcPct.toFixed(1)}%</span> bajada + {neg.service_offer?.items?.length || 0} servicios
              </div>
            </div>
          </div>
          <button onClick={onClose} data-testid="btn-close-duplicar" className="text-white/50 hover:text-white p-1"><XCircle size={22}/></button>
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-2 p-3 border-b border-white/10 bg-white/5">
          <div className="flex items-center gap-1.5 bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 flex-1 min-w-[220px]">
            <Search size={13} className="text-white/40"/>
            <input value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="input-duplicar-search"
              placeholder="Buscar por código, municipio, propietario…"
              className="bg-transparent text-white text-xs outline-none flex-1"/>
          </div>
          <select value={fType} onChange={(e) => setFType(e.target.value)}
            data-testid="select-duplicar-type"
            className="bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs">
            <option value="">Todo tipo</option>
            {typeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={fProvince} onChange={(e) => setFProvince(e.target.value)}
            data-testid="select-duplicar-province"
            className="bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs">
            <option value="">Toda provincia</option>
            {provinceOptions.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <button onClick={toggleAll}
            data-testid="btn-duplicar-toggle-all"
            className="bg-white/10 hover:bg-white/20 text-white text-xs font-black px-3 py-1.5 rounded-lg flex items-center gap-1">
            {selected.size === eligibles.length && eligibles.length > 0
              ? <><Square size={12}/> Ninguno</>
              : <><CheckSquare size={12}/> Todos</>}
          </button>
        </div>

        {/* Tabla */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-white/60 text-sm">
              <Loader2 className="animate-spin mr-2" size={14}/> Cargando cartera…
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#0a0a0a] border-b border-white/10 z-10">
                <tr className="text-white/50 text-[10px] uppercase tracking-wider">
                  <th className="p-2 text-left w-8"></th>
                  <th className="p-2 text-left">Site</th>
                  <th className="p-2 text-left">Municipio · Prov.</th>
                  <th className="p-2 text-left">Tipo</th>
                  <th className="p-2 text-right">Renta anual</th>
                  <th className="p-2 text-right">Nueva ({srcPct.toFixed(1)}%)</th>
                  <th className="p-2 text-left">Fin contrato</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-white/40 italic">Sin candidatos con esos filtros</td></tr>
                )}
                {filtered.map((c) => {
                  const disabled = c.has_active_negotiation || !c.rent_annual;
                  const checked = selected.has(c.code);
                  const nueva = c.rent_annual * (1 - srcPct / 100);
                  return (
                    <tr key={c.code || c.id}
                      onClick={() => !disabled && toggle(c.code)}
                      data-testid={`row-duplicar-${c.code}`}
                      className={`border-b border-white/5 hover:bg-white/5 ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
                      <td className="p-2">
                        <input type="checkbox" checked={checked} disabled={disabled} readOnly
                          className="w-3.5 h-3.5 accent-[#A3FF12]"/>
                      </td>
                      <td className="p-2 text-white font-bold text-xs">
                        {c.code || c.id}
                        {disabled && c.has_active_negotiation && (
                          <span className="ml-2 text-[9px] uppercase text-amber-300">activa</span>
                        )}
                        {disabled && !c.rent_annual && (
                          <span className="ml-2 text-[9px] uppercase text-red-300">sin renta</span>
                        )}
                      </td>
                      <td className="p-2 text-white/70 text-xs">{c.municipality || '—'} · {c.province || '—'}</td>
                      <td className="p-2 text-white/60 text-xs uppercase">{c.type || '—'}</td>
                      <td className="p-2 text-white text-right text-xs">{eur(c.rent_annual)} €</td>
                      <td className="p-2 text-[#A3FF12] text-right text-xs font-black">{eur(nueva)} €</td>
                      <td className="p-2 text-white/60 text-xs">{c.contract_end?.slice(0, 10) || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-3 border-t border-white/10 bg-black/60">
          <div className="text-xs text-white/60">
            <b className="text-white">{selected.size}</b> seleccionados · potencial ahorro ≈{' '}
            <b className="text-[#A3FF12]">
              {eur(Array.from(selected).reduce((sum, code) => {
                const c = candidates.find((x) => x.code === code);
                return sum + (c ? c.rent_annual * (srcPct / 100) : 0);
              }, 0))} €/año
            </b>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose}
              className="text-white/60 hover:text-white text-xs font-black px-3 py-2">Cancelar</button>
            <button onClick={clone} disabled={saving || selected.size === 0}
              data-testid="btn-confirm-duplicar"
              className="bg-[#A3FF12] hover:bg-[#b8ff3a] text-black text-xs font-black px-4 py-2 rounded-lg flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed">
              {saving ? <Loader2 className="animate-spin" size={12}/> : <Copy size={12}/>}
              Duplicar a {selected.size} sites
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DuplicarNegociacionModal;
